import { useCallback, useEffect, useRef } from 'react'

import type {
  PremiumInterval,
  PriceChangeUserAction,
  PriceData,
} from '@flows-ui/services/QuoteDataContext.types'

type UseUserPriceChangedEventOptions = {
  productKey: string
  configurationId?: string
  premiumInterval: PremiumInterval
  flowSessionId: string | undefined
  priceData: PriceData | undefined
  track: (event: string, properties?: Record<string, unknown>) => void
  eventName: string
}

/**
 * Pairs a user action with the resulting price refetch and emits a UserPriceChanged-style
 * event once new price data lands. The caller arms an action by calling the returned function;
 * the hook fires the analytics event when priceData (or premiumInterval, for frequency changes)
 * actually changes.
 */
export const useUserPriceChangedEvent = (options: UseUserPriceChangedEventOptions) => {
  const pendingActionRef = useRef<PriceChangeUserAction | null>(null)
  const previousDataRef = useRef(options.priceData)
  const previousIntervalRef = useRef(options.premiumInterval)

  useEffect(
    function emitUserPriceChangedWhenPriceLands() {
      const syncRefs = () => {
        previousDataRef.current = options.priceData
        previousIntervalRef.current = options.premiumInterval
      }

      const pendingAction = pendingActionRef.current
      if (!pendingAction) {
        syncRefs()
        return
      }
      const hasPriceData = Boolean(options.priceData)
      const dataChanged = options.priceData !== previousDataRef.current
      const intervalChanged = options.premiumInterval !== previousIntervalRef.current
      const shouldFire =
        hasPriceData &&
        (pendingAction === 'payment_frequency_change' ? intervalChanged : dataChanged)

      if (shouldFire) {
        pendingActionRef.current = null
        const productConfiguration = options.priceData?.product_configurations.find((config) => {
          if (options.configurationId) {
            return config.configuration_id === options.configurationId
          }
          return config.product_key === options.productKey
        })
        const priceInfo = productConfiguration?.prices.find(
          (price) => price.premium_interval === options.premiumInterval
        )
        if (productConfiguration && priceInfo) {
          const discounts = productConfiguration.prices
            .filter(
              (price) =>
                price.discount_amount && Number.parseFloat(String(price.discount_amount)) > 0
            )
            .map((price) => ({
              amount: Number.parseFloat(String(price.discount_amount ?? 0)),
              token: price.discount_token,
              context: { discount_group: 'price_discount' },
            }))

          options.track(options.eventName, {
            product_key: options.productKey,
            flow_session_id: options.flowSessionId,
            price: {
              gross_premium: priceInfo.gross_premium,
              payment_frequency: priceInfo.premium_interval,
              gross_premium_after_discount: priceInfo.gross_premium_after_discount,
              net_premium: priceInfo.net_premium,
              tax: priceInfo.tax,
            },
            discounts,
            product_configuration: productConfiguration.configuration_id,
            user_action: pendingAction,
          })
        }
      }

      syncRefs()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.priceData, options.premiumInterval]
  )

  const markPriceChangeAction = useCallback((action: PriceChangeUserAction) => {
    pendingActionRef.current = action
  }, [])

  return markPriceChangeAction
}
